// =============================================================================
// PRODUCT-CARD ADD — Horizon `Component` port of the product-card variant-select →
// <product-form> hidden-id sync (src/js/objects/global/product-card-add.js). Wave-2
// atomic driver-swap: ZERO behaviour/visual change — same inputs → same DOM writes,
// same timing (synchronous within the change dispatch, no rAF/timers), no classes
// toggled, no events dispatched, no preventDefault.
//
// WHAT IT DOES (unchanged): on a card in `card_action: add_to_cart` mode the variant
// <select class="product-card__select"> is a SIBLING of the add-to-cart <product-form>
// (blocks/product-variant-select.liquid + blocks/product-buy-buttons.liquid; the legacy
// direct-render path emits the same markup from snippets/product-card.liquid). The select
// sits OUTSIDE the {% form 'product' %}, so Motto's product-form.js — `new
// FormData(this.form)` on submit + the canSubmitToCart() gate on input[name="id"] and the
// submit button's disabled state — never sees it. On every select change this driver
// mirrors the chosen variant id into the form's hidden input[name="id"] and reflects the
// chosen option's availability on the submit button. Break the mirror and ATC posts the
// server-rendered default variant regardless of the shopper's choice.
//
// HOST: `<product-card-add>` wraps the <product-form> in the ONE shared emitter,
// snippets/_product-card-add-form.liquid (both callers — blocks/product-buy-buttons.liquid
// and the legacy path in snippets/product-card.liquid — render that snippet, so one edit
// point covers every card everywhere). The wrapper is an unstyled inline element around
// the already-unstyled inline <product-form> inside the block-level
// .product-card__buy-buttons div — no CSS anywhere selects bare `product-form` or a
// parent/child chain it interrupts (verified: base.css + src/scss), so the extra node is
// layout-invisible. Motto's product-form.js only walks UP for `quick-add-modal`
// (closest() passes transparently through this wrapper) — zero interception.
//
// LISTENER: the old module bound ONE document-level 'change' listener (bubble phase, no
// options, never removed). The event SOURCE (the select) is a sibling block outside this
// component's subtree, so a declarative on:change/ref cannot reach it; instead each
// instance binds ONE per-instance 'change' listener on its closest('.product-card') root
// (bubble phase, AbortController-scoped, aborted in disconnectedCallback). Card-scoped
// delegation reproduces the document delegation exactly for every reachable DOM shape:
// any .product-card__select change inside the card bubbles through the card root, and the
// handler guards + sync body below are VERBATIM from the old module. No card ancestor →
// bind nothing (the old syncCardVariant returned on `!card` — same inert result).
//
// FIRST-MATCH PARITY (merchant-duplicated sub-blocks): the write path deliberately uses
// the old card.querySelector() FIRST-match queries — NOT this.refs — because with N
// stacked buy-form sub-blocks the old module updated ONLY the first form's input/button.
// N instances each run the same idempotent first-match writes → identical net state.
// ref="hiddenId" / ref="submit" are declared on the owned nodes (ownership/tooling) but
// are intentionally not the write targets.
//
// INJECTED-MARKUP COVERAGE — the custom-element registry's auto-upgrade replaces
// document delegation's DOM-agnostic coverage; per-surface proof:
//   1. PDP <product-recommendations> AJAX (THE LIVE SURFACE — templates/product.json has
//      card_action=add_to_cart with no picked collection, so ATC cards exist ONLY
//      post-injection): assets/global.js fetches the section and innerHTML-swaps it.
//      Scripts inside innerHTML never execute, so sections/product-recommendations.liquid
//      emits this module from its initial-HTML SHELL (both source branches); the injected
//      <product-card-add> nodes then upgrade on insertion → connectedCallback rebinds.
//   2. PLP/search facets.js #ProductGridContainer innerHTML re-render: replaced cards
//      upgrade on insertion; the initially-EMPTY-grid edge (zero cards → no per-card
//      script in initial HTML) is closed by section-level emission in
//      main-collection-product-grid.liquid + main-search.liquid.
//   3. Theme editor shopify:section:load / block re-renders: replaced nodes upgrade →
//      connect; removed nodes disconnect → listeners aborted.
//   4. Swiper clones: no loop-mode Swiper drives any ATC card surface today (productSlider
//      has no loop param; product-grid.js/product-with-image.js hardcode loop:false). If
//      loop ever appears, cloneNode copies upgrade on document insertion → covered.
//   5. recently-viewed / quickview / cart-recommendations / predictive-search: no
//      .product-card__select ATC markup today — not surfaces. Rebuy renders its own
//      rebuy-* classes — independent, untouched.
//
// MOTTO BUTTON CO-OWNERSHIP: product-form.js (vendored, PROTECTED) also mutates the same
// submit button (loading class, aria-disabled, spinner, and an unconditional
// removeAttribute('disabled') + dataset.status='available' in its post-add .finally).
// This driver touches button.disabled ONLY inside the change handler — never on
// connect/re-render — exactly like the old module, so it never fights the vendored engine.
//
// RETIRED WITH THE OLD MODULE: the window.__productCardAddInit idempotence flag is NOT
// reused — the per-instance connect/disconnect lifecycle replaces it. Until the serial
// rebuild strips the barrel import from assets/global.autojs.js, the old document
// listener and this driver both fire on a change: both write the SAME values to the SAME
// first-match nodes (idempotent) — benign mid-wave double-drive, no ordering hazard.
// window.formatMoney is not used here (it belongs to product-info-column-component.js /
// product-info.js — the latter is OUT of this wave).
//
// `type="module"` scripts defer and dedupe per URL — the shared snippet may emit N copies
// (one per card) plus the section-shell copies; the module loads once. Native ESM via the
// `@theme/*` importmap (snippets/horizon-importmap.liquid) — NOT a webpack src/js bundle
// entry; do not feed it through webpack.
//
// UNTRACKED new file (alongside assets/component.js + assets/utilities.js) — re-verify it
// exists before any push; a parallel agent's git op on assets/ can remove it.
// =============================================================================
import { Component } from '@theme/component';

// Selector strings VERBATIM from the old module — the first-match semantics on
// merchant-duplicated sub-blocks depend on them staying identical.
const SELECT_SELECTOR = '.product-card__select';
const CARD_SELECTOR = '.product-card';
const HIDDEN_ID_SELECTOR = '.product-card__buy-form input[name="id"]';
const SUBMIT_SELECTOR = '.product-card__buy-form [type="submit"][name="add"]';

/**
 * The product-card add-to-cart sync custom element (`<product-card-add>`), wrapping the
 * shared card <product-form> (snippets/_product-card-add-form.liquid).
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} [hiddenId] - this form's hidden input[name="id"] (declared
 *   for ownership; writes go through the card-scoped first-match query — see header).
 * @property {HTMLButtonElement} [submit] - this form's submit button (same note).
 *
 * @extends {Component<Refs>}
 */
export class ProductCardAdd extends Component {
  // Aborts the per-instance card-root 'change' listener on disconnect (the old module's
  // single document listener was never removed; the port scopes it to the instance).
  #listeners = null;

  connectedCallback() {
    super.connectedCallback(); // populates this.refs + wires the base MutationObserver

    this.#listeners?.abort(); // defensive: a double-connect without a disconnect must not double-bind
    this.#listeners = new AbortController();

    // The select is a SIBLING block — delegate from the shared card root, exactly like
    // the old document delegation but instance-scoped. No card ancestor → stay inert
    // (parity: the old syncCardVariant bailed on `!card` for any such select anyway).
    const card = this.closest(CARD_SELECTOR);
    if (!card) return;

    // Bubble phase, no capture/passive/once — same phase + options as the old listener.
    card.addEventListener('change', this.#onChange, { signal: this.#listeners.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback(); // the base class disconnects its MutationObserver here
    this.#listeners?.abort();
    this.#listeners = null;
  }

  // Handler guards VERBATIM from the old module's onChange.
  #onChange = (event) => {
    const target = event.target;
    if (!target || typeof target.matches !== 'function') return;
    if (!target.matches(SELECT_SELECTOR)) return;
    this.#syncCardVariant(target);
  };

  // Sync body VERBATIM from the old module's syncCardVariant: mirror one card's chosen
  // variant into its (first) add-to-cart form + reflect availability on the button.
  #syncCardVariant(select) {
    const card = select.closest(CARD_SELECTOR);
    if (!card) return;

    const hiddenId = card.querySelector(HIDDEN_ID_SELECTOR);
    if (hiddenId) hiddenId.value = select.value;

    // The chosen <option> carries `disabled` for sold-out variants. A disabled option
    // can't normally be selected, but reflect its state on the submit button defensively
    // (and re-enable on an available one).
    const option = select.options[select.selectedIndex];
    const submit = card.querySelector(SUBMIT_SELECTOR);
    if (submit && option) {
      submit.disabled = Boolean(option.disabled);
    }
  }
}

if (!customElements.get('product-card-add')) {
  customElements.define('product-card-add', ProductCardAdd);
}
